export default defineAppConfig({
  ui: {
    strategy: 'override',
    badge: {
      slots: {
        base: 'font-medium inline-flex items-center',
        label: 'truncate',
        leadingIcon: 'shrink-0 !size-4',
        leadingAvatar: 'shrink-0',
        leadingAvatarSize: '',
        trailingIcon: 'shrink-0',
      },
      variants: {
        buttonGroup: {
          horizontal:
            'not-only:first:rounded-e-none not-only:last:rounded-s-none not-last:not-first:rounded-none focus-visible:z-[1]',
          vertical:
            'not-only:first:rounded-b-none not-only:last:rounded-t-none not-last:not-first:rounded-none focus-visible:z-[1]',
        },
        color: {
          primary: 'primary-600 ',
          secondary: 'secondary-600 ',
          success: 'success-600 ',
          info: 'info-600 ',
          warning: 'warning-600 ',
          error: 'error-600 ',
          neutral: 'neutral-600 ',
        },
        variant: {
          solid: '',
          outline: '',
          soft: '',
          subtle: '',
        },
        size: {
          xs: {
            base: 'text-[8px]/3 px-1 py-0.5 gap-1 rounded-sm',
            leadingIcon: 'size-3',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-3',
          },
          sm: {
            base: 'text-[10px]/3 px-1.5 py-1 gap-1 rounded-sm',
            leadingIcon: 'size-3',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-3',
          },
          md: {
            base: 'text-xs px-2 py-1 gap-1 rounded-md',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
          },
          lg: {
            base: 'text-sm px-2 py-1 gap-1.5 rounded-md',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
          },
          xl: {
            base: 'text-base px-2.5 py-1 gap-1.5 rounded-md',
            leadingIcon: 'size-6',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-6',
          },
        },
        square: {
          true: '',
        },
      },
      compoundVariants: [
        {
          color: 'warning',
          variant: 'soft',
          class: 'bg-warning-500/10 text-warning-500',
        },
        {
          color: 'primary',
          variant: 'solid',
          class: 'bg-primary text-inverted',
        },
        {
          color: 'primary',
          variant: 'outline',
          class: 'text-primary ring ring-inset ring-primary/50',
        },
        {
          color: 'primary',
          variant: 'soft',
          class: 'bg-primary/10 text-primary',
        },
        {
          color: 'primary',
          variant: 'subtle',
          class: 'bg-primary/10 text-primary ring ring-inset ring-primary/25',
        },
        {
          color: 'neutral',
          variant: 'solid',
          class: 'text-inverted bg-inverted',
        },
        {
          color: 'neutral',
          variant: 'outline',
          class: 'ring ring-inset ring-accented text-default bg-default',
        },
        {
          color: 'neutral',
          variant: 'soft',
          class: 'text-default bg-elevated',
        },
        {
          color: 'neutral',
          variant: 'subtle',
          class: 'ring ring-inset ring-accented text-default bg-elevated',
        },
        {
          size: 'xs',
          square: true,
          class: 'p-0.5',
        },
        {
          size: 'sm',
          square: true,
          class: 'p-1',
        },
        {
          size: 'md',
          square: true,
          class: 'p-1',
        },
        {
          size: 'lg',
          square: true,
          class: 'p-1',
        },
        {
          size: 'xl',
          square: true,
          class: 'p-1',
        },
      ],
      defaultVariants: {
        color: 'primary',
        variant: 'solid',
        size: 'md',
      },
    },
    collapsible: {
      slots: {
        root: 'w-full',
        content:
          'data-[state=open]:animate-[collapsible-down_200ms_ease-out] data-[state=closed]:animate-[collapsible-up_200ms_ease-out] overflow-hidden w-full',
      },
    },
    fileUpload: {
      slots: {
        root: 'relative flex flex-col cursor-pointer',
        base: [
          'w-full flex-1 bg-default border border-default flex flex-col gap-2 items-stretch justify-center rounded-lg focus-visible:outline-2',
          'transition-[background]',
        ],
        wrapper: 'flex flex-col items-center justify-center text-center',
        icon: 'shrink-0',
        avatar: 'shrink-0',
        label: 'font-medium text-default mt-2',
        description: 'text-muted mt-1',
        actions: 'flex flex-wrap gap-1.5 shrink-0 mt-4',
        files: '',
        file: 'relative',
        fileLeadingAvatar: 'shrink-0',
        fileWrapper: 'flex flex-col min-w-0',
        fileName: 'text-default truncate',
        fileSize: 'text-muted truncate',
        fileTrailingButton: '',
      },
      variants: {
        color: {
          primary: '',
          secondary: '',
          success: '',
          info: '',
          warning: '',
          error: '',
          neutral: '',
        },
        variant: {
          area: {
            wrapper: 'px-4 py-3',
            base: 'p-4',
          },
          button: {},
        },
        size: {
          xs: {
            base: 'text-xs',
            icon: 'size-4',
            file: 'text-xs px-2 py-1 gap-1',
            fileWrapper: 'flex-row gap-1',
          },
          sm: {
            base: 'text-xs',
            icon: 'size-4',
            file: 'text-xs px-2.5 py-1.5 gap-1.5',
            fileWrapper: 'flex-row gap-1',
          },
          md: {
            base: 'text-sm',
            icon: 'size-5',
            file: 'text-xs px-2.5 py-1.5 gap-1.5',
          },
          lg: {
            base: 'text-sm',
            icon: 'size-5',
            file: 'text-sm px-3 py-2 gap-2',
            fileSize: 'text-xs',
          },
          xl: {
            base: 'text-base',
            icon: 'size-6',
            file: 'text-sm px-3 py-2 gap-2',
          },
        },
        layout: {
          list: {
            root: 'gap-2 items-start',
            files: 'flex flex-col w-full gap-2',
            file: 'min-w-0 flex items-center border border-default rounded-md w-full',
            fileTrailingButton: 'ms-auto',
          },
          grid: {
            fileWrapper: 'hidden',
            fileLeadingAvatar:
              'size-full rounded-lg flex items-center justify-center [&>img]:object-contain [&>img]:max-w-full [&>img]:max-h-full',
            fileTrailingButton:
              'absolute -top-1.5 -end-1.5 p-0 rounded-full border-2 border-bg',
          },
        },
        position: {
          inside: '',
          outside: '',
        },
        dropzone: {
          true: 'border-dashed data-[dragging=true]:bg-elevated/25',
        },
        interactive: {
          true: '',
        },
        highlight: {
          true: '',
        },
        multiple: {
          true: '',
        },
        disabled: {
          true: 'cursor-not-allowed opacity-75',
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          class: 'focus-visible:outline-primary',
        },
        {
          color: 'primary',
          highlight: true,
          class: 'border-primary',
        },
        {
          color: 'neutral',
          class: 'focus-visible:outline-inverted',
        },
        {
          color: 'neutral',
          highlight: true,
          class: 'border-inverted',
        },
        {
          size: 'xs',
          layout: 'list',
          class: {
            fileTrailingButton: '-me-1',
          },
        },
        {
          size: 'sm',
          layout: 'list',
          class: {
            fileTrailingButton: '-me-1.5',
          },
        },
        {
          size: 'md',
          layout: 'list',
          class: {
            fileTrailingButton: '-me-1.5',
          },
        },
        {
          size: 'lg',
          layout: 'list',
          class: {
            fileTrailingButton: '-me-2',
          },
        },
        {
          size: 'xl',
          layout: 'list',
          class: {
            fileTrailingButton: '-me-2',
          },
        },
        {
          variant: 'button',
          size: 'xs',
          class: {
            base: 'p-1',
          },
        },
        {
          variant: 'button',
          size: 'sm',
          class: {
            base: 'p-1.5',
          },
        },
        {
          variant: 'button',
          size: 'md',
          class: {
            base: 'p-1.5',
          },
        },
        {
          variant: 'button',
          size: 'lg',
          class: {
            base: 'p-2',
          },
        },
        {
          variant: 'button',
          size: 'xl',
          class: {
            base: 'p-2',
          },
        },
        {
          layout: 'grid',
          multiple: true,
          class: {
            files: 'grid grid-cols-2 md:grid-cols-3 gap-4 w-full',
            file: 'p-0 aspect-square',
          },
        },
        {
          layout: 'grid',
          multiple: false,
          class: {
            file: 'absolute inset-0 p-0',
          },
        },
        {
          interactive: true,
          disabled: false,
          class: 'hover:bg-elevated/25',
        },
      ],
      defaultVariants: {
        color: 'primary',
        variant: 'area',
        size: 'md',
      },
    },
    toast: {
      slots: {
        root: 'relative group overflow-hidden bg-default shadow-lg rounded-lg ring ring-default p-4 flex gap-2.5 focus:outline-none',
        wrapper: 'w-0 flex-1 flex flex-col',
        title: 'text-sm font-medium text-highlighted',
        description: 'text-sm text-muted',
        icon: 'shrink-0 size-5',
        avatar: 'shrink-0',
        avatarSize: '2xl',
        actions: 'flex gap-1.5 shrink-0',
        progress: 'absolute inset-x-0 bottom-0 h-1 z-[-1] bg-primary-600',
        close: 'p-0',
      },
      variants: {
        color: {
          primary: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
            icon: 'text-primary',
            progress: 'bg-primary-600',
          },
          secondary: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-secondary',
            icon: 'text-secondary',
            progress: 'bg-secondary-600',
          },
          success: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-success',
            icon: 'text-success',
            progress: 'bg-success-600',
          },
          info: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-info',
            icon: 'text-info',
            progress: 'bg-info-600',
          },
          warning: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-warning',
            icon: 'text-warning',
            progress: 'bg-warning-600',
          },
          error: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-error',
            icon: 'text-error',
            progress: 'bg-error-600',
          },
          neutral: {
            root: 'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted',
            icon: 'text-highlighted',
            progress: 'bg-inverted-600',
          },
        },
        orientation: {
          horizontal: {
            root: 'items-center',
            actions: 'items-center',
          },
          vertical: {
            root: 'items-start',
            actions: 'items-start mt-2.5',
          },
        },
        title: {
          true: {
            description: 'mt-1',
          },
        },
      },
      defaultVariants: {
        color: 'primary',
      },
    },

    progress: {
      slots: {
        root: 'gap-2',
        base: 'relative overflow-hidden rounded-full bg-accented',
        indicator:
          'rounded-full size-full transition-transform duration-200 ease-out',
        status: 'flex justify-end text-dimmed transition-[width] duration-200',
        steps: 'grid items-end',
        step: 'truncate text-end row-start-1 col-start-1 transition-opacity',
      },
      variants: {
        animation: {
          carousel: '',
          'carousel-inverse': '',
          swing: '',
          elastic: '',
        },
        color: {
          primary: {
            indicator: 'bg-primary-600',
            steps: 'text-primary',
          },
          secondary: {
            indicator: 'bg-neutral-600',
            steps: 'text-secondary',
          },
          success: {
            indicator: 'bg-success-600',
            steps: 'text-success',
          },
          info: {
            indicator: 'bg-info-600',
            steps: 'text-info',
          },
          warning: {
            indicator: 'bg-warning-600',
            steps: 'text-warning',
          },
          error: {
            indicator: 'bg-error-600',
            steps: 'text-error',
          },
          neutral: {
            indicator: 'bg-inverted',
            steps: 'text-inverted',
          },
        },
        size: {
          '2xs': {
            status: 'text-xs',
            steps: 'text-xs',
          },
          xs: {
            status: 'text-xs',
            steps: 'text-xs',
          },
          sm: {
            status: 'text-sm',
            steps: 'text-sm',
          },
          md: {
            status: 'text-sm',
            steps: 'text-sm',
          },
          lg: {
            status: 'text-sm',
            steps: 'text-sm',
          },
          xl: {
            status: 'text-base',
            steps: 'text-base',
          },
          '2xl': {
            status: 'text-base',
            steps: 'text-base',
          },
        },
        step: {
          active: {
            step: 'opacity-100',
          },
          first: {
            step: 'opacity-100 text-muted',
          },
          other: {
            step: 'opacity-0',
          },
          last: {
            step: '',
          },
        },
        orientation: {
          horizontal: {
            root: 'w-full flex flex-col',
            base: 'w-full',
            status: 'flex-row',
          },
          vertical: {
            root: 'h-full flex flex-row-reverse',
            base: 'h-full',
            status: 'flex-col',
          },
        },
        inverted: {
          true: {
            status: 'self-end',
          },
        },
      },
      compoundVariants: [
        {
          inverted: true,
          orientation: 'horizontal',
          class: {
            step: 'text-start',
            status: 'flex-row-reverse',
          },
        },
        {
          inverted: true,
          orientation: 'vertical',
          class: {
            steps: 'items-start',
            status: 'flex-col-reverse',
          },
        },
        {
          orientation: 'horizontal',
          size: '2xs',
          class: 'h-px',
        },
        {
          orientation: 'horizontal',
          size: 'xs',
          class: 'h-0.5',
        },
        {
          orientation: 'horizontal',
          size: 'sm',
          class: 'h-1',
        },
        {
          orientation: 'horizontal',
          size: 'md',
          class: 'h-2',
        },
        {
          orientation: 'horizontal',
          size: 'lg',
          class: 'h-3',
        },
        {
          orientation: 'horizontal',
          size: 'xl',
          class: 'h-4',
        },
        {
          orientation: 'horizontal',
          size: '2xl',
          class: 'h-5',
        },
        {
          orientation: 'vertical',
          size: '2xs',
          class: 'w-px',
        },
        {
          orientation: 'vertical',
          size: 'xs',
          class: 'w-0.5',
        },
        {
          orientation: 'vertical',
          size: 'sm',
          class: 'w-1',
        },
        {
          orientation: 'vertical',
          size: 'md',
          class: 'w-2',
        },
        {
          orientation: 'vertical',
          size: 'lg',
          class: 'w-3',
        },
        {
          orientation: 'vertical',
          size: 'xl',
          class: 'w-4',
        },
        {
          orientation: 'vertical',
          size: '2xl',
          class: 'w-5',
        },
        {
          orientation: 'horizontal',
          animation: 'carousel',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[carousel_2s_ease-in-out_infinite] data-[state=indeterminate]:rtl:animate-[carousel-rtl_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'vertical',
          animation: 'carousel',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[carousel-vertical_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'horizontal',
          animation: 'carousel-inverse',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[carousel-inverse_2s_ease-in-out_infinite] data-[state=indeterminate]:rtl:animate-[carousel-inverse-rtl_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'vertical',
          animation: 'carousel-inverse',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[carousel-inverse-vertical_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'horizontal',
          animation: 'swing',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[swing_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'vertical',
          animation: 'swing',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[swing-vertical_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'horizontal',
          animation: 'elastic',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[elastic_2s_ease-in-out_infinite]',
          },
        },
        {
          orientation: 'vertical',
          animation: 'elastic',
          class: {
            indicator:
              'data-[state=indeterminate]:animate-[elastic-vertical_2s_ease-in-out_infinite]',
          },
        },
      ],
      defaultVariants: {
        animation: 'carousel',
        color: 'primary',
        size: 'md',
      },
    },
    checkbox: {
      slots: {
        root: 'rounded-xs relative flex items-start',
        container: 'flex items-center',
        base: 'rounded-xs cursor-pointer z-20 h-4 w-4 ring-offset-0 ring-1 ring-[#D1D1D6] border-neutral-300 overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-2 hover:ring-[#AEAEB2] focus:hover:ring-[#AEAEB2] focus:ring-1 focus:ring-[#D1D1D6] hover:ring-offset-0 focus:ring-offset-0 transition-colors duration-300',
        indicator:
          'flex items-center justify-center size-full text-neutral-50 bg-inherit transition-colors duration-300',
        icon: 'shrink-0 size-full',
        wrapper: 'w-full',
        label: 'block font-normal text-sm text-neutral-600',
        description: 'text-neutral-600 text-xs',
      },
      variants: {
        color: {
          primary: {
            base: 'focus-visible:outline-primary checked:ring-primary-600 checked:hover:ring-primary-600 focus:checked:hover:ring-primary-600 checked:focus:ring-primary-600',
            indicator: 'bg-primary-600',
          },
          secondary: {
            base: 'focus-visible:outline-secondary',
            indicator: 'bg-secondary',
          },
          success: {
            base: 'focus-visible:outline-success',
            indicator: 'bg-success',
          },
          info: {
            base: 'focus-visible:outline-info',
            indicator: 'bg-info',
          },
          warning: {
            base: 'focus-visible:outline-warning',
            indicator: 'bg-warning',
          },
          error: {
            base: 'focus-visible:outline-error',
            indicator: 'bg-error',
          },
          neutral: {
            base: 'focus-visible:outline-inverted',
            indicator: 'bg-inverted',
          },
        },
        variant: {
          list: {
            root: 'rounded-sm',
          },
          card: {
            root: 'border border-muted rounded-lg',
          },
        },
        indicator: {
          start: {
            root: 'flex-row',
            wrapper: 'ms-2',
          },
          end: {
            root: 'flex-row-reverse',
            wrapper: 'me-2',
          },
          hidden: {
            base: 'sr-only',
            wrapper: 'text-center',
          },
        },
        size: {
          xs: {
            base: 'size-3',
            container: 'h-4',
            wrapper: 'text-xs',
          },
          sm: {
            base: 'size-3.5',
            container: 'h-4',
            wrapper: 'text-xs',
          },
          md: {
            base: 'h-4 w-4',
            container: 'h-5',
            wrapper: 'text-sm',
          },
          lg: {
            base: 'size-4.5',
            container: 'h-5',
            wrapper: 'text-sm',
          },
          xl: {
            base: 'size-5',
            container: 'h-6',
            wrapper: 'text-base',
          },
        },
        required: {
          true: {
            label: "after:content-['*'] after:ms-0.5 after:text-error",
          },
        },
        disabled: {
          true: {
            base: 'cursor-not-allowed opacity-75',
            label: 'cursor-not-allowed opacity-75',
            description: 'cursor-not-allowed opacity-75',
          },
        },
        checked: {
          true: '',
        },
      },
      compoundVariants: [
        {
          size: 'xs',
          variant: 'card',
          class: {
            root: 'p-2.5',
          },
        },
        {
          size: 'sm',
          variant: 'card',
          class: {
            root: 'p-3',
          },
        },
        {
          size: 'md',
          variant: 'card',
          class: {
            root: 'p-3.5',
          },
        },
        {
          size: 'lg',
          variant: 'card',
          class: {
            root: 'p-4',
          },
        },
        {
          size: 'xl',
          variant: 'card',
          class: {
            root: 'p-4.5',
          },
        },
        {
          color: 'primary',
          variant: 'card',
          class: {
            root: 'has-data-[state=checked]:border-primary',
          },
        },
        {
          color: 'neutral',
          variant: 'card',
          class: {
            root: 'has-data-[state=checked]:border-inverted',
          },
        },
        {
          variant: 'card',
          disabled: true,
          class: {
            root: 'cursor-not-allowed opacity-75',
          },
        },
      ],
      defaultVariants: {
        size: 'lg',
        color: 'primary',
        variant: 'list',
        indicator: 'start',
      },
    },
    switch: {
      slots: {
        root: 'relative flex items-start',
        base: [
          'cursor-pointer inline-flex items-center shrink-0 rounded-full border-2 border-transparent focus-visible:outline-2 focus-visible:outline-offset-2 data-[state=unchecked]:bg-neutral-200',
          'transition-[background] duration-200',
        ],
        container: 'flex items-center',
        thumb:
          'group pointer-events-none rounded-full bg-neutral-50 shadow-lg ring-0 transition-transform duration-200 data-[state=unchecked]:translate-x-0 data-[state=unchecked]:rtl:-translate-x-0 flex items-center justify-center',
        icon: [
          'absolute shrink-0 group-data-[state=unchecked]:text-dimmed opacity-0 size-10/12',
          'transition-[color,opacity] duration-200',
        ],
        wrapper: 'ms-2',
        label: 'block font-medium text-default',
        description: 'text-muted',
      },
      variants: {
        color: {
          primary: {
            base: 'data-[state=checked]:bg-primary-600 focus-visible:outline-primary data-[state=checked]:hover:bg-primary-400 data-[state=unchecked]:hover:bg-primary-400',
            icon: 'group-data-[state=checked]:text-primary',
          },
          secondary: {
            base: 'data-[state=checked]:bg-secondary focus-visible:outline-secondary',
            icon: 'group-data-[state=checked]:text-secondary',
          },
          success: {
            base: 'data-[state=checked]:bg-success focus-visible:outline-success',
            icon: 'group-data-[state=checked]:text-success',
          },
          info: {
            base: 'data-[state=checked]:bg-info focus-visible:outline-info',
            icon: 'group-data-[state=checked]:text-info',
          },
          warning: {
            base: 'data-[state=checked]:bg-warning focus-visible:outline-warning',
            icon: 'group-data-[state=checked]:text-warning',
          },
          error: {
            base: 'data-[state=checked]:bg-error focus-visible:outline-error',
            icon: 'group-data-[state=checked]:text-error',
          },
          neutral: {
            base: 'data-[state=checked]:bg-inverted focus-visible:outline-inverted',
            icon: 'group-data-[state=checked]:text-highlighted',
          },
        },
        size: {
          xs: {
            base: 'w-7',
            container: 'h-4',
            thumb:
              'size-3 data-[state=checked]:translate-x-3 data-[state=checked]:rtl:-translate-x-3',
            wrapper: 'text-xs',
          },
          sm: {
            base: 'w-9 h-5',
            container: 'h-4',
            thumb:
              'h-4 w-4 data-[state=checked]:translate-x-4 data-[state=checked]:rtl:-translate-x-3',
            wrapper: 'text-xs',
          },
          md: {
            base: 'w-14 h-6',
            container: 'h-5',
            thumb:
              'h-5 w-5 data-[state=checked]:translate-x-8 data-[state=checked]:rtl:-translate-x-3.5',
            wrapper: 'text-sm',
          },
          lg: {
            base: 'w-10',
            container: 'h-5',
            thumb:
              'size-4.5 data-[state=checked]:translate-x-4.5 data-[state=checked]:rtl:-translate-x-4.5',
            wrapper: 'text-sm',
          },
          xl: {
            base: 'w-11',
            container: 'h-6',
            thumb:
              'size-5 data-[state=checked]:translate-x-5 data-[state=checked]:rtl:-translate-x-5',
            wrapper: 'text-base',
          },
        },
        checked: {
          true: {
            icon: 'group-data-[state=checked]:opacity-100',
          },
        },
        unchecked: {
          true: {
            icon: 'group-data-[state=unchecked]:opacity-100',
          },
        },
        loading: {
          true: {
            icon: 'animate-spin',
          },
        },
        required: {
          true: {
            label: "after:content-['*'] after:ms-0.5 after:text-error",
          },
        },
        disabled: {
          true: {
            base: 'cursor-not-allowed opacity-75',
            label: 'cursor-not-allowed opacity-75',
            description: 'cursor-not-allowed opacity-75',
          },
        },
      },
      defaultVariants: {
        color: 'primary',
        size: 'md',
      },
    },
    table: {
      slots: {
        root: 'relative overflow-auto ',
        base: 'overflow-x-auto',
        caption: 'sr-only',
        thead:
          'sticky top-0 z-40 [&>tr]:after:absolute [&>tr]:after:inset-x-0 [&>tr]:after:bottom-0 [&>tr]:after:h-px [&>tr]:after:bg-transparent',
        tbody:
          'divide-none [&>tr]:data-[selectable=true]:hover:bg-elevated/50 [&>tr]:data-[selectable=true]:focus-visible:outline-primary',
        tfoot: 'relative',
        tr: 'data-[selected=true]:bg-elevated/50',
        th: 'font-bold px-4 py-1.5 text-base text-highlighted text-left rtl:text-right [&:has([role=checkbox])]:px-3',
        td: 'p-0 text-base text-muted whitespace-nowrap [&:has([role=checkbox])]:px-3',
        empty: 'py-6 text-center text-sm text-muted',
        loading: 'py-6 text-center',
      },
      variants: {
        pinned: {
          true: {
            th: 'sticky bg-default/75 data-[pinned=left]:left-0 data-[pinned=right]:right-0',
            td: 'sticky bg-default/75 data-[pinned=left]:left-0 data-[pinned=right]:right-0',
          },
        },
        sticky: {
          true: {
            thead: 'sticky top-0 inset-x-0 bg-default/75 z-[1] backdrop-blur',
            tfoot:
              'sticky bottom-0 inset-x-0 bg-default/75 z-[1] backdrop-blur',
          },
        },
        loading: {
          true: {
            thead: 'after:absolute after:bottom-0 after:inset-x-0 after:h-px',
          },
        },
        loadingAnimation: {
          carousel: '',
          'carousel-inverse': '',
          swing: '',
          elastic: '',
        },
        loadingColor: {
          primary: 'primary-600',
          secondary: 'primary-600',
          success: '',
          info: '',
          warning: '',
          error: '',
          neutral: '',
        },
      },
      compoundVariants: [
        {
          loadingColor: 'secondary',
          class: {
            tr: 'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_5rem]',
            th: 'grid-column: span 1 py-2 px-0 bg-primary-300 min-w-0',
            td: 'grid-column: span 1 py-0 px-0 min-w-0 overflow-hidden whitespace-normal',
          },
        },
        {
          loadingColor: 'primary',
          class: {
            thead: 'bg-neutral-100',
            th: 'font-[1rem]',
            td: 'font-[1rem]',
          },
        },
      ],
      defaultVariants: {
        loadingColor: 'primary',
        loadingAnimation: 'carousel',
      },
    },
    dropdownMenu: {
      slots: {
        content:
          'min-w-32 bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-dropdown-menu-content-transform-origin) flex flex-col',
        viewport:
          'relative divide-y divide-default scroll-py-1 overflow-y-auto flex-1',
        arrow: 'fill-default',
        group: 'p-1 isolate',
        label: 'w-full flex items-center font-semibold text-highlighted',
        separator: '-mx-1 my-1 h-px bg-border',
        item: 'cursor-pointer rounded hover:bg-primary-200 group relative w-full flex items-center select-none outline-none before:absolute before:z-[-1] before:inset-px before:rounded-md data-disabled:cursor-not-allowed data-disabled:opacity-75',
        itemLeadingIcon: 'shrink-0',
        itemLeadingAvatar: 'shrink-0',
        itemLeadingAvatarSize: '',
        itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
        itemTrailingIcon: 'shrink-0',
        itemTrailingKbds: 'hidden lg:inline-flex items-center shrink-0',
        itemTrailingKbdsSize: '',
        itemLabel: 'truncate',
        itemLabelExternalIcon: 'inline-block size-3 align-top text-dimmed',
      },
      variants: {
        color: {
          primary: 'text-primary-600',
          secondary: 'text-primary-600',
          success: '',
          info: '',
          warning: '',
          error: '',
          neutral: '',
        },
        active: {
          true: {
            item: 'text-highlighted before:bg-elevated',
            itemLeadingIcon: 'text-default',
          },
          false: {
            item: [
              'text-default data-highlighted:text-highlighted data-[state=open]:text-highlighted data-highlighted:before:bg-elevated/50 data-[state=open]:before:bg-elevated/50',
              'transition-colors before:transition-colors',
            ],
            itemLeadingIcon: [
              'text-dimmed group-data-highlighted:text-default group-data-[state=open]:text-default',
              'transition-colors',
            ],
          },
        },
        loading: {
          true: {
            itemLeadingIcon: 'animate-spin',
          },
        },
        size: {
          xs: {
            label: 'p-1 text-xs gap-1',
            item: 'p-1 text-xs gap-1',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemTrailingIcon: 'size-4',
            itemTrailingKbds: 'gap-0.5',
            itemTrailingKbdsSize: 'sm',
          },
          sm: {
            label: 'p-1.5 text-xs gap-1.5',
            item: 'p-1.5 text-xs gap-1.5',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemTrailingIcon: 'size-4',
            itemTrailingKbds: 'gap-0.5',
            itemTrailingKbdsSize: 'sm',
          },
          md: {
            label: 'p-1.5 text-sm gap-1.5',
            item: 'p-1.5 text-sm gap-1.5',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemTrailingIcon: 'size-5',
            itemTrailingKbds: 'gap-0.5',
            itemTrailingKbdsSize: 'md',
          },
          lg: {
            label: 'p-2 text-sm gap-2',
            item: 'p-2 text-sm gap-2',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemTrailingIcon: 'size-5',
            itemTrailingKbds: 'gap-1',
            itemTrailingKbdsSize: 'md',
          },
          xl: {
            label: 'p-2 text-base gap-2',
            item: 'p-2 text-base gap-2',
            itemLeadingIcon: 'size-6',
            itemLeadingAvatarSize: 'xs',
            itemTrailingIcon: 'size-6',
            itemTrailingKbds: 'gap-1',
            itemTrailingKbdsSize: 'lg',
          },
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          active: false,
          class: {
            item: 'text-primary-600 data-highlighted:text-primary-600 data-highlighted:before:bg-primary/10 data-[state=open]:before:bg-primary/10',
            itemLeadingIcon:
              'text-primary-600/75 group-data-highlighted:text-primary-600 group-data-[state=open]:text-primary-600',
          },
        },
        {
          color: 'primary',
          active: true,
          class: {
            item: 'text-primary-600 before:bg-primary/10',
            itemLeadingIcon: 'text-primary-600',
          },
        },
        {
          color: 'secondary',
          active: false,
          class: {
            item: 'text-primary-600 before:bg-primary/10',
            itemLeadingIcon: 'text-primary-600',
          },
        },
        {
          color: 'secondary',
          active: true,
          class: {
            item: 'text-primary-600 before:bg-primary/10',
            itemLeadingIcon: 'text-primary-600',
          },
        },
      ],
      defaultVariants: {
        size: 'md',
      },
    },
    select: {
      slots: {
        base: [
          'relative group rounded-md inline-flex items-center focus:outline-none disabled:cursor-not-allowed disabled:opacity-75',
          'transition-colors',
        ],
        leading: 'absolute inset-y-0 start-0 flex items-center',
        leadingIcon: 'shrink-0 text-primary-600',
        leadingAvatar: 'shrink-0',
        leadingAvatarSize: '',
        trailing: 'absolute inset-y-0 end-0 flex items-center',
        trailingIcon: 'shrink-0',
        value: 'truncate pointer-events-none',
        placeholder: 'truncate text-primary-600',
        arrow: 'fill-default',
        content:
          'max-h-60 w-(--reka-select-trigger-width) bg-default shadow-lg rounded-md ring ring-default overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-select-content-transform-origin) pointer-events-auto flex flex-col',
        viewport:
          'relative divide-y divide-default scroll-py-1 overflow-y-auto flex-1',
        group: 'p-1 isolate',
        empty: 'text-center text-muted',
        label: 'font-semibold text-highlighted',
        separator: '-mx-1 my-1 h-px bg-border',
        item: [
          'group relative w-full flex items-center select-none outline-none before:absolute before:z-[-1] before:inset-px before:rounded-md data-disabled:cursor-not-allowed data-disabled:opacity-75 text-default data-highlighted:not-data-disabled:text-highlighted data-highlighted:not-data-disabled:before:bg-elevated/50',
          'transition-colors before:transition-colors',
        ],
        itemLeadingIcon: [
          'shrink-0 text-dimmed group-data-highlighted:not-group-data-disabled:text-default',
          'transition-colors',
        ],
        itemLeadingAvatar: 'shrink-0',
        itemLeadingAvatarSize: '',
        itemLeadingChip: 'shrink-0',
        itemLeadingChipSize: '',
        itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
        itemTrailingIcon: 'shrink-0',
        itemLabel: 'truncate',
      },
      variants: {
        buttonGroup: {
          horizontal:
            'not-only:first:rounded-e-none not-only:last:rounded-s-none not-last:not-first:rounded-none focus-visible:z-[1]',
          vertical:
            'not-only:first:rounded-b-none not-only:last:rounded-t-none not-last:not-first:rounded-none focus-visible:z-[1]',
        },
        size: {
          xs: {
            base: 'px-2 py-1 text-xs gap-1',
            leading: 'ps-2',
            trailing: 'pe-2',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
            label: 'p-1 text-[10px]/3 gap-1',
            item: 'p-1 text-xs gap-1',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemLeadingChip: 'size-4',
            itemLeadingChipSize: 'sm',
            itemTrailingIcon: 'size-4',
            empty: 'p-1 text-xs',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-xs gap-1.5',
            leading: 'ps-2.5',
            trailing: 'pe-2.5',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
            label: 'p-1.5 text-[10px]/3 gap-1.5',
            item: 'p-1.5 text-xs gap-1.5',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemLeadingChip: 'size-4',
            itemLeadingChipSize: 'sm',
            itemTrailingIcon: 'size-4',
            empty: 'p-1.5 text-xs',
          },
          md: {
            base: 'px-2.5 py-1.5 text-sm gap-1.5',
            leading: 'ps-2.5',
            trailing: 'pe-2.5',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
            label: 'p-1.5 text-xs gap-1.5',
            item: 'p-1.5 text-sm gap-1.5',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemLeadingChip: 'size-5',
            itemLeadingChipSize: 'md',
            itemTrailingIcon: 'size-5',
            empty: 'p-1.5 text-sm',
          },
          lg: {
            base: 'px-3 py-2 text-sm gap-2',
            leading: 'ps-3',
            trailing: 'pe-3',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
            label: 'p-2 text-xs gap-2',
            item: 'p-2 text-sm gap-2',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemLeadingChip: 'size-5',
            itemLeadingChipSize: 'md',
            itemTrailingIcon: 'size-5',
            empty: 'p-2 text-sm',
          },
          xl: {
            base: 'px-3 py-2 text-base gap-2',
            leading: 'ps-3',
            trailing: 'pe-3',
            leadingIcon: 'size-6',
            leadingAvatarSize: 'xs',
            trailingIcon: 'size-6',
            label: 'p-2 text-sm gap-2',
            item: 'p-2 text-base gap-2',
            itemLeadingIcon: 'size-6',
            itemLeadingAvatarSize: 'xs',
            itemLeadingChip: 'size-6',
            itemLeadingChipSize: 'lg',
            itemTrailingIcon: 'size-6',
            empty: 'p-2 text-base',
          },
        },
        variant: {
          outline: 'text-highlighted bg-default ring ring-inset ring-accented',
          soft: 'text-highlighted bg-elevated/50 hover:bg-elevated focus:bg-elevated disabled:bg-elevated/50',
          subtle: 'text-highlighted bg-elevated ring ring-inset ring-accented',
          ghost:
            'text-highlighted bg-transparent hover:bg-elevated focus:bg-elevated disabled:bg-transparent dark:disabled:bg-transparent',
          none: 'text-highlighted bg-transparent',
        },
        color: {
          primary: 'text-primary-600',
          secondary: 'text-secondary-600',
          success: 'text-success-600',
          info: 'text-info-600',
          warning: 'text-warning-600',
          error: 'text-error-600',
          neutral: '',
        },
        leading: {
          true: '',
        },
        trailing: {
          true: '',
        },
        loading: {
          true: '',
        },
        highlight: {
          true: '',
        },
        type: {
          file: 'file:me-1.5 file:font-medium file:text-muted file:outline-none',
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['outline', 'subtle'],
          class: 'focus:ring-2 focus:ring-inset focus:ring-primary rounded-sm ',
        },
        {
          color: 'primary',
          variant: ['soft'],
          class: {
            base: 'min-w-48 rounded-sm cursor-pointer bg-primary-600 hover:bg-primary-400 focus:bg-primary-400 disabled:bg-primary-200 aria-disabled:bg-primary-200 focus:outline-none focus-visible:ring-0 focus-visible:ring-primary-600',
            value: 'text-neutral-50',
            trailingIcon: 'text-neutral-50',
            leadingIcon: 'text-neutral-50 size-4',
            item: 'cursor-pointer text-primary-600',
            itemLeadingIcon: 'text-primary-600',
            placeholder: 'text-neutral-50',
          },
        },
        {
          color: 'primary',
          highlight: true,
          class: 'ring ring-inset ring-primary',
        },
        {
          color: 'neutral',
          variant: ['outline', 'subtle'],
          class: 'focus:ring-2 focus:ring-inset focus:ring-inverted',
        },
        {
          color: 'neutral',
          highlight: true,
          class: 'ring ring-inset ring-inverted',
        },
        {
          leading: true,
          size: 'xs',
          class: 'ps-7',
        },
        {
          leading: true,
          size: 'sm',
          class: 'ps-8',
        },
        {
          leading: true,
          size: 'md',
          class: 'ps-9',
        },
        {
          leading: true,
          size: 'lg',
          class: 'ps-10',
        },
        {
          leading: true,
          size: 'xl',
          class: 'ps-11',
        },
        {
          trailing: true,
          size: 'xs',
          class: 'pe-7',
        },
        {
          trailing: true,
          size: 'sm',
          class: 'pe-8',
        },
        {
          trailing: true,
          size: 'md',
          class: 'pe-9',
        },
        {
          trailing: true,
          size: 'lg',
          class: 'pe-10',
        },
        {
          trailing: true,
          size: 'xl',
          class: 'pe-11',
        },
        {
          loading: true,
          leading: true,
          class: {
            leadingIcon: 'animate-spin',
          },
        },
        {
          loading: true,
          leading: false,
          trailing: true,
          class: {
            trailingIcon: 'animate-spin',
          },
        },
      ],
      defaultVariants: {
        size: 'md',
        color: 'primary',
        variant: 'outline',
      },
    },
    selectMenu: {
      slots: {
        base: [
          'relative group rounded-sm inline-flex items-center focus:outline-none disabled:cursor-not-allowed disabled:opacity-75 bg-neutral-50',
          'transition-colors',
        ],
        leading: '',
        leadingIcon: '',
        leadingAvatar: 'shrink-0',
        leadingAvatarSize: '',
        trailing: 'absolute inset-y-0 end-0 flex items-center',
        trailingIcon: 'shrink-0 text-primary-600',
        value: 'truncate pointer-events-none',
        placeholder: 'truncate',
        arrow: 'fill-primary-600',
        content: [
          'max-h-60 w-(--reka-select-trigger-width) bg-neutral-50 shadow-lg rounded-sm ring-0 overflow-hidden data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-select-content-transform-origin) pointer-events-auto flex flex-col',
          'origin-(--reka-combobox-content-transform-origin) w-(--reka-combobox-trigger-width)',
        ],
        viewport:
          'relative divide-y divide-neutral-200 scroll-py-1 overflow-y-auto flex-1 p-0',
        group: 'p-0 isolate',
        empty: 'py-2 text-center text-neutral-600',
        label: 'font-semibold text-primary-600',
        separator: '-mx-1 my-1 h-px border-t border-neutral-200',
        item: [
          'group relative w-full flex items-center select-none outline-none before:absolute before:z-[-1] before:rounded-sm data-disabled:cursor-not-allowed data-disabled:opacity-50 text-primary-600 data-highlighted:not-data-disabled:text-primary-600 data-highlighted:not-data-disabled:before:bg-primary-200 hover:bg-primary-200 cursor-pointer',
          'transition-colors before:transition-colors px-2 py-1.5 pl-8',
        ],
        itemLeadingIcon: [
          'shrink-0 text-primary-600 group-data-highlighted:not-group-data-disabled:text-primary-600',
          'transition-colors h-5 w-5',
        ],
        itemLeadingAvatar: 'shrink-0',
        itemLeadingAvatarSize: '',
        itemLeadingChip: 'shrink-0',
        itemLeadingChipSize: '',
        itemTrailing: 'ms-auto inline-flex gap-1.5 items-center',
        itemTrailingIcon: 'shrink-0 h-5 w-5 text-neutral-600',
        itemLabel: 'truncate',
        input:
          'border-b border-neutral-200 bg-transparent text-neutral-900 placeholder-neutral-600 focus:ring-0 focus:outline-none',
        focusScope: 'flex flex-col min-h-0',
      },
      variants: {
        buttonGroup: {
          horizontal:
            'not-only:first:rounded-e-none not-only:last:rounded-s-none not-last:not-first:rounded-none focus-visible:z-[1]',
          vertical:
            'not-only:first:rounded-b-none not-only:last:rounded-t-none not-last:not-first:rounded-none focus-visible:z-[1]',
        },
        size: {
          xs: {
            base: 'px-2 py-1 text-xs gap-1 h-8',
            leading: 'ps-2',
            trailing: 'pe-2',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
            label: 'p-1 text-[10px]/3 gap-1',
            item: 'p-1 text-xs gap-1',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemLeadingChip: 'size-4',
            itemLeadingChipSize: 'sm',
            itemTrailingIcon: 'size-4',
            input: 'px-2 py-1 text-xs',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-sm gap-1.5 h-10',
            leading: 'ps-2.5',
            trailing: 'pe-2.5',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
            label: 'p-1.5 text-[10px]/3 gap-1.5',
            item: 'p-1.5 text-sm gap-1.5',
            itemLeadingIcon: 'size-4',
            itemLeadingAvatarSize: '3xs',
            itemLeadingChip: 'size-4',
            itemLeadingChipSize: 'sm',
            itemTrailingIcon: 'size-4',
            input: 'px-2.5 py-0.5 text-sm',
          },
          md: {
            base: 'px-3 py-2 text-base gap-2',
            leading: 'ps-3',
            trailing: 'pe-3',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
            label: 'p-1.5 text-xs gap-1.5',
            item: 'p-2 text-base gap-2',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemLeadingChip: 'size-5',
            itemLeadingChipSize: 'md',
            itemTrailingIcon: 'size-5',
            input: 'px-3 py-2 text-sm',
          },
          lg: {
            base: 'px-3 py-2.5 text-lg gap-2',
            leading: 'ps-3',
            trailing: 'pe-3',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
            label: 'p-2 text-xs gap-2',
            item: 'p-2.5 text-lg gap-2',
            itemLeadingIcon: 'size-5',
            itemLeadingAvatarSize: '2xs',
            itemLeadingChip: 'size-5',
            itemLeadingChipSize: 'md',
            itemTrailingIcon: 'size-5',
            input: 'px-3 py-2.5 text-lg',
          },
          xl: {
            base: 'px-3.5 py-2.5 text-base gap-2.5',
            leading: 'ps-3.5',
            trailing: 'pe-3.5',
            leadingIcon: 'size-6',
            leadingAvatarSize: 'xs',
            trailingIcon: 'size-6',
            label: 'p-2 text-sm gap-2',
            item: 'p-2.5 text-base gap-2.5',
            itemLeadingIcon: 'size-6',
            itemLeadingAvatarSize: 'xs',
            itemLeadingChip: 'size-6',
            itemLeadingChipSize: 'lg',
            itemTrailingIcon: 'size-6',
            input: 'px-3.5 py-2.5 text-xl',
          },
        },
        variant: {
          outline:
            'ring ring-inset ring-neutral-300 focus:ring-1 focus:ring-primary-600 cursor-pointer',
          soft: 'focus:bg-primary-200 disabled:bg-neutral-100',
          subtle: 'text-primary-600 ',
          ghost:
            'text-primary-600 bg-transparent hover:bg-primary-200 focus:bg-primary-200 disabled:bg-transparent',
          none: '',
        },
        color: {
          primary: 'hover:bg-primary-200 text-primary-600 bg-neutral-50',
          secondary: '',
          success: '',
          info: '',
          warning: '',
          error: '',
          neutral: 'hover:bg-primary-200 text-primary-600 bg-neutral-100',
        },
        leading: {
          true: '',
        },
        trailing: {
          true: '',
        },
        loading: {
          true: '',
        },
        highlight: {
          true: '',
        },
        type: {
          file: 'file:me-1.5 file:font-medium file:text-neutral-600 file:outline-none',
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: ['subtle', 'solid'],
          class:
            'ring-0 bg-neutral-50 text-primary-600 hover:bg-primary-200 cursor-pointer',
        },
        {
          color: 'neutral',
          variant: ['subtle', 'none'],
          class: {
            base: 'ring-0 ring-none bg-neutral-50 text-neutral-600 hover:bg-primary-200 cursor-pointer border-neutral-300 border-1 border-solid outline-none',
            item: 'text-neutral-600',
          },
        },
        {
          color: 'primary',
          variant: ['outline'],
          class:
            'ring-0 ring-none bg-neutral-50 text-primary-600 hover:bg-primary-200 cursor-pointer border-primary-600 border-1 border-solid outline-none',
        },
        {
          color: 'error',
          variant: ['outline'],
          class:
            'ring-0 ring-none bg-neutral-100 text-error-600 hover:bg-primary-200 cursor-pointer border-error-600 border-1 border-solid outline-none',
        },
        {
          color: 'neutral',
          variant: ['outline'],
          class: {
            value: 'text-neutral-600',
            base: 'ring-0 focus:ring-0 hover:ring-0 rounded-[0.5rem] ring-none bg-neutral-50 text-neutral-300 hover:bg-neutral-100 cursor-pointer border-neutral-300 border-1 border-solid outline-none h-8 w-[15rem] text-neutral-600',
            leadingIcon: 'text-neutral-600',
            trailingIcon: 'text-neutral-600',
            placeholder: 'text-neutral-600',
            item: 'text-neutral-600 px-3',
            itemLeadingIcon:
              'text-neutral-600 group-data-highlighted:not-group-data-disabled:text-neutral-600',
            itemTrailingIcon: 'text-neutral-600',
            itemLabel: 'text-neutral-600',
            itemLeadingAvatar: 'text-neutral-600',
            itemLeadingChip: 'text-neutral-600',
            itemLeadingChipSize: 'text-neutral-600',
          },
        },
        {
          leading: true,
          size: 'xs',
          class: 'ps-7',
        },
        {
          leading: true,
          size: 'sm',
          class: 'ps-9',
        },
        {
          leading: true,
          size: 'md',
          class: 'ps-9',
        },
        {
          leading: true,
          size: 'lg',
          class: 'ps-10',
        },
        {
          leading: true,
          size: 'xl',
          class: 'ps-11',
        },
        {
          trailing: true,
          size: 'xs',
          class: 'pe-7',
        },
        {
          trailing: true,
          size: 'sm',
          class: 'pe-8',
        },
        {
          trailing: true,
          size: 'md',
          class: 'pe-9',
        },
        {
          trailing: true,
          size: 'lg',
          class: 'pe-10',
        },
        {
          trailing: true,
          size: 'xl',
          class: 'pe-11',
        },
        {
          loading: true,
          leading: true,
          class: {
            leadingIcon: 'animate-spin',
          },
        },
        {
          loading: true,
          leading: false,
          trailing: true,
          class: {
            trailingIcon: 'animate-spin',
          },
        },
      ],
      defaultVariants: {
        size: 'md',
        color: 'white',
        variant: 'none',
      },
    },
    modal: {
      slots: {
        overlay: 'fixed inset-0 bg-elevated/75',
        content:
          'border-primary-600 border-2 border-solid bg-neutral-50 fixed divide-y divide-default flex flex-col focus:outline-none py-6 px-4',
        header: 'flex items-center gap-1.5 p-4 sm:px-6 min-h-16',
        wrapper: '',
        body: 'flex-1 overflow-y-auto py-4',
        footer: 'flex items-center gap-1.5',
        title: 'text-highlighted font-semibold',
        description: 'mt-1 text-muted text-sm',
        close: 'absolute top-4 end-4',
      },
      variants: {
        transition: {
          true: {
            overlay:
              'data-[state=open]:animate-[fade-in_200ms_ease-out] data-[state=closed]:animate-[fade-out_200ms_ease-in]',
            content:
              'data-[state=open]:animate-[scale-in_200ms_ease-out] data-[state=closed]:animate-[scale-out_200ms_ease-in]',
          },
        },
        fullscreen: {
          true: {
            content:
              'fixed bottom-0 left-0 right-0 h-fit top-auto border-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] rounded-[1rem_1rem_0rem_0rem]',
            overlay: 'bg-elevated/75 pb-10',
          },
          false: {
            content:
              'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100vw-2rem)] max-w-lg max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-4rem)] rounded-lg shadow-lg ring ring-default overflow-hidden',
          },
        },
      },
    },
    pagination: {
      slots: {
        root: 'flex items-center last:border-r-0',
        list: 'flex items-center gap-0',
        ellipsis:
          'pointer-events-none px-2.5 py-1.5 text-xs bg-neutral-50 text-primary-600',
        label: 'min-w-5 text-center text-primary-600',
        first:
          'bg-neutral-50 border-none ring-1 ring-neutral-200 shadow-none hover:bg-primary-600 hover:text-neutral-50 text-primary-600 rounded-none',
        prev: 'bg-neutral-50 ring-1 ring-neutral-200 shadow-none hover:bg-primary-600 hover:text-neutral-50 text-primary-600 rounded-none border-r border-neutral-200',
        item: 'bg-neutral-50 hover:bg-primary-600 !hover:text-neutral-50 text-neutral-50 rounded-none ring-0 border-t border-b border-r border-neutral-200',
        next: 'bg-neutral-50 border-none ring-1 ring-neutral-200 shadow-none hover:bg-primary-600 hover:text-neutral-50 text-primary-600 rounded-none',
        last: 'bg-neutral-50 border-none ring-1 ring-neutral-200 shadow-none hover:bg-primary-600 hover:text-neutral-50 text-primary-600 rounded-none',
      },

      defaultVariants: {
        size: 'sm',
      },
    },
    formField: {
      slots: {
        root: '',
        wrapper: '',
        labelWrapper: 'flex content-center items-center justify-between',
        label: 'ml-1 block font-medium text-neutral-700',
        container: 'mt-1 relative',
        description: 'text-muted',
        error: 'mt-1 ml-1 text-error text-xs text-error-600',
        hint: 'text-muted',
        help: 'mt-2 text-muted',
      },
      variants: {
        size: {
          xs: {
            root: 'text-xs',
          },
          sm: {
            root: 'text-xs',
          },
          md: {
            root: 'text-sm',
          },
          lg: {
            root: 'text-sm',
          },
          xl: {
            root: 'text-base',
          },
        },
        required: {
          true: {
            label: "after:content-['*'] after:ms-0.5 after:text-red-500",
          },
        },
      },
      defaultVariants: {
        size: 'md',
      },
    },
    textarea: {
      variants: {
        variant: {
          outline:
            'shadow-sm bg-neutral-50 text-neutral-900 ring-1 ring-inset ring-neutral-300 hover:ring-primary-600 focus:ring-1 focus:ring-neutral-300 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-neutral-300',
        },
      },
    },
    input: {
      slots: {
        root: 'relative inline-flex items-center',
        base: [
          'relative block w-full rounded-sm border-0 placeholder:text-neutral-600 focus:outline-none disabled:cursor-not-allowed disabled:opacity-75',
          'transition-colors',
        ],
        leading: 'absolute inset-y-0 start-0 flex items-center',
        leadingIcon: 'shrink-0 text-neutral-600',
        leadingAvatar: 'shrink-0',
        leadingAvatarSize: '',
        trailing: 'absolute inset-y-0 end-0 flex items-center',
        trailingIcon: 'shrink-0 text-neutral-600',
      },
      variants: {
        buttonGroup: {
          horizontal: {
            root: 'group has-focus-visible:z-[1]',
            base: 'group-not-only:group-first:rounded-e-none group-not-only:group-last:rounded-s-none group-not-last:group-not-first:rounded-none',
          },
          vertical: {
            root: 'group has-focus-visible:z-[1]',
            base: 'group-not-only:group-first:rounded-b-none group-not-only:group-last:rounded-t-none group-not-last:group-not-first:rounded-none',
          },
        },
        size: {
          '2xs': {
            base: 'px-2 py-1 text-xs gap-1',
            leading: 'ps-2',
            trailing: 'pe-2',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
          },
          xs: {
            base: 'px-2.5 py-1 text-xs gap-1',
            leading: 'ps-2.5',
            trailing: 'pe-2.5',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-xs gap-1.5',
            leading: 'ps-2.5',
            trailing: 'pe-2.5',
            leadingIcon: 'size-5',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-5',
          },
          md: {
            base: 'px-3 py-1.5 text-sm gap-1.5',
            leading: 'ps-3',
            trailing: 'pe-3',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
          },
          lg: {
            base: 'px-3.5 py-2 text-sm gap-2',
            leading: 'ps-3.5',
            trailing: 'pe-3.5',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
          },
          xl: {
            base: 'px-3.5 py-2 text-base gap-2 min-h-[3rem]',
            leading: 'ps-3.5',
            trailing: 'pe-3.5',
            leadingIcon: 'size-6',
            leadingAvatarSize: 'xs',
            trailingIcon: 'size-6',
          },
        },
        variant: {
          outline:
            'shadow-sm bg-neutral-50 text-neutral-900 ring-1 ring-inset ring-neutral-300 hover:ring-primary-600 focus:ring-1 focus:ring-neutral-300 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-neutral-300',
          none: 'bg-transparent focus:ring-0 focus:shadow-none',
        },
        color: {
          white: '',
          neutral: '',
          primary: '',
          secondary: '',
          success: '',
          info: '',
          warning: '',
          error: '!ring-error-600 !focus:ring-error-600 !ring-1',
        },
        leading: {
          true: '',
        },
        trailing: {
          true: '',
        },
        loading: {
          true: '',
        },
        highlight: {
          true: '',
        },
        type: {
          file: 'file:mr-1.5 file:font-medium file:text-neutral-600 file:bg-transparent file:border-0 file:p-0 file:outline-none',
        },
      },
      compoundVariants: [
        {
          color: 'white',
          variant: 'outline',
          class:
            'shadow-sm bg-neutral-50 text-black ring-1 ring-inset ring-neutral-300 focus:ring-1 focus:ring-primary-600',
        },
        {
          color: 'neutral',
          variant: 'outline',
          class:
            'shadow-sm bg-neutral-50 text-neutral-900 ring-1 ring-inset ring-neutral-300 hover:ring-primary-600 focus:ring-1 focus:ring-neutral-300 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-neutral-300',
        },
        {
          color: 'primary',
          highlight: true,
          class: 'ring ring-inset ring-primary-600',
        },
        {
          leading: true,
          size: '2xs',
          class: 'ps-7',
        },
        {
          leading: true,
          size: 'xs',
          class: 'ps-8',
        },
        {
          leading: true,
          size: 'sm',
          class: 'ps-9',
        },
        {
          leading: true,
          size: 'md',
          class: 'ps-10',
        },
        {
          leading: true,
          size: 'lg',
          class: 'ps-10',
        },
        {
          leading: true,
          size: 'xl',
          class: 'ps-11',
        },
        {
          trailing: true,
          size: '2xs',
          class: 'pe-7',
        },
        {
          trailing: true,
          size: 'xs',
          class: 'pe-8',
        },
        {
          trailing: true,
          size: 'sm',
          class: 'pe-8',
        },
        {
          trailing: true,
          size: 'md',
          class: 'pe-9',
        },
        {
          trailing: true,
          size: 'lg',
          class: 'pe-10',
        },
        {
          trailing: true,
          size: 'xl',
          class: 'pe-11',
        },
        {
          loading: true,
          leading: true,
          class: {
            leadingIcon: 'animate-spin',
          },
        },
        {
          loading: true,
          leading: false,
          trailing: true,
          class: {
            trailingIcon: 'animate-spin',
          },
        },
      ],
      defaultVariants: {
        size: 'sm',
        color: 'white',
        variant: 'outline',
      },
    },
    button: {
      slots: {
        base: [
          'rounded-xs cursor-pointer font-medium inline-flex items-center disabled:cursor-not-allowed aria-disabled:cursor-not-allowed disabled:opacity-75 aria-disabled:opacity-75',
          'transition-colors',
        ],
        label: 'truncate',
        leadingIcon: 'shrink-0',
        leadingAvatar: 'shrink-0',
        leadingAvatarSize: '',
        trailingIcon: 'shrink-0',
      },
      variants: {
        buttonGroup: {
          horizontal:
            'not-only:first:rounded-e-none not-only:last:rounded-s-none not-last:not-first:rounded-none focus-visible:z-[1]',
          vertical:
            'not-only:first:rounded-b-none not-only:last:rounded-t-none not-last:not-first:rounded-none focus-visible:z-[1]',
        },
        color: {
          primary: '',
          secondary: '',
          success: '',
          info: '',
          warning: '',
          error: '!bg-error-600 !text-neutral-50 hover:!bg-error-600',
          neutral: '',
        },
        variant: {
          solid: '',
          outline: '',
          soft: '',
          subtle: '',
          ghost: '',
          link: '',
        },
        size: {
          xs: {
            base: 'px-2 py-1 text-xs gap-1',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
          },
          sm: {
            base: 'px-2.5 py-1.5 text-xs gap-1.5',
            leadingIcon: 'size-4',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-4',
          },
          md: {
            base: 'px-2.5 py-1.5 text-sm gap-1.5',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
          },
          lg: {
            base: 'px-3 py-2 text-sm gap-2',
            leadingIcon: 'size-5',
            leadingAvatarSize: '2xs',
            trailingIcon: 'size-5',
          },
          xl: {
            base: 'px-3 py-4 text-base gap-2 min-h-[3rem]',
            leadingIcon: 'size-6',
            leadingAvatarSize: 'xs',
            trailingIcon: 'size-6',
          },
        },
        block: {
          true: {
            base: 'w-full justify-center',
            trailingIcon: 'ms-auto',
          },
        },
        square: {
          true: '',
        },
        leading: {
          true: '',
        },
        trailing: {
          true: '',
        },
        loading: {
          true: '',
        },
        active: {
          true: {
            base: '',
          },
          false: {
            base: '',
          },
        },
      },
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class:
            'justify-between cursor-pointer font-mainFont !bg-primary-600 font-bold text-neutral-50 rounded-xs px-4 py-2.5 hover:!bg-primary-400 focus:!bg-primary-600 active:!bg-primary-600',
        },
        {
          color: 'primary',
          variant: 'solid',
          loading: true,
          class:
            'cursor-pointer font-mainFont !bg-primary-600 font-bold text-neutral-50 rounded mx-auto py-2.5',
        },
        {
          color: 'primary',
          variant: 'solid',
          size: 'sm',
          class:
            'cursor-pointer font-mainFont !bg-primary-600 font-bold text-neutral-50 rounded px-2py-0',
        },
        {
          color: 'primary',
          variant: 'solid',
          active: true,
          class:
            'cursor-pointer font-mainFont !bg-primary-600 font-bold text-neutral-50 rounded px-4 py-2.5',
        },
        {
          color: 'primary',
          variant: 'solid',
          active: false,
          class:
            'cursor-pointer font-mainFont !bg-primary-600 font-bold text-neutral-50 rounded px-4 py-2.5 hover:!bg-primary-400',
        },
        {
          color: 'primary',
          variant: 'outline',
          class:
            'ring ring-inset ring-primary-600 text-primary-600 hover:bg-primary-200 disabled:bg-transparent aria-disabled:bg-transparent dark:disabled:bg-transparent dark:aria-disabled:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        },
        {
          color: 'primary',
          variant: 'soft',
          class:
            'text-primary-600 bg-primary-200 hover:bg-primary-300 focus:outline-none focus-visible:bg-primary-300 disabled:bg-primary-200 aria-disabled:bg-primary-200 rounded-xs',
        },
        {
          color: 'primary',
          variant: 'subtle',
          class:
            'text-primary-600 ring ring-inset ring-primary-600/25 bg-primary-200 hover:bg-primary-300 disabled:bg-primary-200 aria-disabled:bg-primary-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600',
        },
        {
          color: 'primary',
          variant: 'ghost',
          class:
            'text-primary-600 hover:bg-primary-200 focus:outline-none focus-visible:bg-primary-200 disabled:bg-transparent aria-disabled:bg-transparent dark:disabled:bg-transparent dark:aria-disabled:bg-transparent',
        },
        {
          color: 'primary',
          variant: 'link',
          class:
            'bg-primary-600 !text-neutral-50 font-bold font-mainFont hover:bg-primary-400 px-4 py-2',
        },
        {
          color: 'secondary',
          variant: 'outline',
          class:
            'bg-neutral-50 rounded-xs ring ring-inset ring-primary-600 text-primary-600 hover:bg-primary-200 disabled:bg-transparent aria-disabled:bg-transparent dark:disabled:bg-transparent dark:aria-disabled:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        },
        {
          color: 'secondary',
          variant: 'outline',
          active: true,
          class:
            'ring-primary-600 hover:ring-primary-400 cursor-pointer font-mainFont bg-primary-600 text-neutral-50 hover:bg-primary-400 rounded',
        },
        {
          color: 'secondary',
          variant: 'soft',
          class:
            'justify-center ring-none cursor-pointer font-mainFont bg-neutral-50 text-primary-600 hover:bg-primary-200 rounded-xs',
        },
        {
          color: 'neutral',
          variant: 'solid',
          class:
            'text-inverted bg-inverted hover:bg-inverted/90 disabled:bg-inverted aria-disabled:bg-inverted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-inverted',
        },
        {
          color: 'neutral',
          variant: 'outline',
          class:
            'ring ring-inset ring-accented text-default bg-default hover:bg-elevated disabled:bg-default aria-disabled:bg-default focus:outline-none focus-visible:ring-2 focus-visible:ring-inverted',
        },
        {
          color: 'neutral',
          variant: 'soft',
          class:
            'justify-center ring-none cursor-pointer font-mainFont bg-neutral-100 text-primary-600 hover:bg-primary-200 rounded-xs',
        },
        {
          size: 'xs',
          square: true,
          class: 'p-1',
        },
        {
          size: 'sm',
          square: true,
          class: 'p-1.5',
        },
        {
          size: 'md',
          square: true,
          class: 'p-1.5',
        },
        {
          size: 'lg',
          square: true,
          class: 'p-2',
        },
        {
          size: 'xl',
          square: true,
          class: 'min-h-[3rem] px-4 py-0',
        },
        {
          loading: true,
          leading: true,
          class: {
            leadingIcon: 'animate-spin',
          },
        },
        {
          loading: true,
          leading: false,
          trailing: true,
          class: {
            trailingIcon: 'animate-spin',
          },
        },
      ],
      defaultVariants: {
        color: 'primary',
        variant: 'solid',
        size: 'md',
      },
    },
    popover: {
      slots: {
        content:
          'bg-neutral-50 shadow-lg rounded-md ring ring-neutral-300 data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] origin-(--reka-popover-content-transform-origin) focus:outline-none pointer-events-auto',
        arrow: 'fill-neutral-50',
      },
    },
    slideover: {
      slots: {
        overlay: 'fixed inset-0 bg-elevated/75',
        content:
          'fixed bg-default divide-y divide-default sm:ring ring-default sm:shadow-lg flex flex-col focus:outline-none',
        header: 'flex items-center gap-1.5 p-4 sm:px-6 min-h-16',
        wrapper: '',
        body: 'flex-1 overflow-y-auto p-4 sm:p-6',
        footer: 'flex items-center gap-1.5 p-4 sm:px-6',
        title: 'text-highlighted font-semibold',
        description: 'mt-1 text-muted text-sm',
        close: 'absolute top-4 end-4',
      },
      variants: {
        side: {
          top: {
            content: 'inset-x-0 top-0 max-h-full',
          },
          right: {
            content: 'right-0 inset-y-0 w-full max-w-md',
          },
          bottom: {
            content: 'inset-x-0 bottom-0 max-h-full',
          },
          left: {
            content: 'left-0 inset-y-0 w-full max-w-md',
          },
        },
        transition: {
          true: {
            overlay:
              'data-[state=open]:animate-[fade-in_200ms_ease-out] data-[state=closed]:animate-[fade-out_200ms_ease-in]',
          },
        },
      },
      compoundVariants: [
        {
          transition: true,
          side: 'top',
          class: {
            content:
              'data-[state=open]:animate-[slide-in-from-top_200ms_ease-in-out] data-[state=closed]:animate-[slide-out-to-top_200ms_ease-in-out]',
          },
        },
        {
          transition: true,
          side: 'right',
          class: {
            content:
              'data-[state=open]:animate-[slide-in-from-right_200ms_ease-in-out] data-[state=closed]:animate-[slide-out-to-right_200ms_ease-in-out]',
          },
        },
        {
          transition: true,
          side: 'bottom',
          class: {
            content:
              'data-[state=open]:animate-[slide-in-from-bottom_200ms_ease-in-out] data-[state=closed]:animate-[slide-out-to-bottom_200ms_ease-in-out]',
          },
        },
        {
          transition: true,
          side: 'left',
          class: {
            content:
              'data-[state=open]:animate-[slide-in-from-left_200ms_ease-in-out] data-[state=closed]:animate-[slide-out-to-left_200ms_ease-in-out]',
          },
        },
      ],
    },
    calendar: {
      slots: {
        root: '',
        header: 'flex items-center justify-between',
        body: 'flex flex-col space-y-4 pt-4 sm:flex-row sm:space-x-4 sm:space-y-0',
        heading:
          'text-center font-bold truncate mx-auto font-mainFont text-neutral-600',
        grid: 'w-full border-collapse select-none space-y-1 focus:outline-none',
        gridRow: 'grid grid-cols-7 place-items-center',
        gridWeekDaysRow: 'mb-1 grid w-full grid-cols-7',
        gridBody: 'grid',
        headCell: 'rounded-md ',
        cell: 'relative text-center bg-neutral-50',
        cellTrigger: [
          'm-0.5 relative flex items-center justify-center rounded-full whitespace-nowrap focus-visible:ring-2 focus:outline-none data-disabled:text-muted data-unavailable:line-through data-unavailable:text-muted data-unavailable:pointer-events-none data-[selected]:text-inverted data-today:font-semibold data-[outside-view]:text-muted',
          'transition cursor-pointer bg-neutral-50',
        ],
      },
      variants: {
        color: {
          primary: {
            headCell: 'text-neutral-600',
            cellTrigger:
              'focus-visible:ring-primary-600 data-[selected]:bg-primary-600! data-today:not-data-[selected]:text-primary-600 data-[highlighted]:bg-primary-200 hover:not-data-[selected]:bg-primary-200',
          },
          secondary: {
            headCell: 'text-secondary',
            cellTrigger:
              'focus-visible:ring-secondary data-[selected]:bg-secondary data-today:not-data-[selected]:text-secondary data-[highlighted]:bg-secondary/20 hover:not-data-[selected]:bg-secondary/20',
          },
          success: {
            headCell: 'text-success',
            cellTrigger:
              'focus-visible:ring-success data-[selected]:bg-success data-today:not-data-[selected]:text-success data-[highlighted]:bg-success/20 hover:not-data-[selected]:bg-success/20',
          },
          info: {
            headCell: 'text-info',
            cellTrigger:
              'focus-visible:ring-info data-[selected]:bg-info data-today:not-data-[selected]:text-info data-[highlighted]:bg-info/20 hover:not-data-[selected]:bg-info/20',
          },
          warning: {
            headCell: 'text-warning',
            cellTrigger:
              'focus-visible:ring-warning data-[selected]:bg-warning data-today:not-data-[selected]:text-warning data-[highlighted]:bg-warning/20 hover:not-data-[selected]:bg-warning/20',
          },
          error: {
            headCell: 'text-error',
            cellTrigger:
              'focus-visible:ring-error data-[selected]:bg-error data-today:not-data-[selected]:text-error data-[highlighted]:bg-error/20 hover:not-data-[selected]:bg-error/20',
          },
          neutral: {
            headCell: 'text-highlighted',
            cellTrigger:
              'focus-visible:ring-inverted data-[selected]:bg-inverted data-today:not-data-[selected]:text-highlighted data-[highlighted]:bg-inverted/20 hover:not-data-[selected]:bg-inverted/10',
          },
        },
        size: {
          xs: {
            heading: 'text-sm',
            cell: 'text-xs',
            headCell: 'text-[10px]',
            cellTrigger: 'size-7',
            body: 'space-y-2 pt-2',
          },
          sm: {
            heading: 'text-xs',
            headCell: 'text-xs',
            cell: 'text-xs',
            cellTrigger: 'size-7',
          },
          md: {
            heading: 'text-sm',
            headCell: 'text-xs',
            cell: 'text-sm',
            cellTrigger: 'size-8',
          },
          lg: {
            heading: 'text-base',
            headCell: 'text-base',
            cellTrigger: 'size-9 text-base',
          },
          xl: {
            heading: 'text-lg',
            headCell: 'text-lg',
            cellTrigger: 'size-10 text-lg',
          },
        },
      },
      defaultVariants: {
        size: 'md',
        color: 'primary',
      },
    },
  },

  APP_VERSION: '1.0.0',
  GIT_REVISION: 'fffffff',
})
